/**
 * TenantHomeV2 — new tenant landing shipped by the design pass.
 *
 * Design intent (per /app/design_guidelines.json):
 *   Field Notes × Stripe × Notion. Left-aligned Swiss typography, dense
 *   two-column action grid, Rent-on-Time Score front-and-center.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, PenTool, MessageSquare, Star, Users, Sparkles, ArrowRight, Wrench, FileText, Receipt, ShieldCheck, MessagesSquare } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { getPersonalizedGreeting } from "@/lib/personalizer";
import { api } from "@/lib/api";

export default function TenantHomeV2() {
  const { user } = useAuth() || {};
  const t = useT();
  const [score, setScore] = useState(null);
  const [dueDays, setDueDays] = useState(null);

  useEffect(() => {
    api.get("/tenant/score").then((r) => setScore(r.data)).catch(() => setScore(null));
    // Rough "next payment" hint — the real dashboard fetches leases; we
    // keep this optional so the page renders cleanly even without data.
    api.get("/tenant/next-due").then((r) => setDueDays(r.data?.days_until_due ?? null)).catch(() => setDueDays(null));
  }, []);

  const greeting = useMemo(() => getPersonalizedGreeting(user, t), [user, t]);

  const nextDueLabel = dueDays == null
    ? t("v2.tenant_no_due", "You're up to date. Enjoy your day.")
    : dueDays <= 0
      ? t("v2.tenant_due_now", "Rent is due today. Tap Pay rent below.")
      : t("v2.tenant_due_in_days", `Next payment due in ${dueDays} day${dueDays === 1 ? "" : "s"}.`);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="tenant-home-v2">
      <main className="flex-1 overflow-y-auto">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-6 sticky top-0 z-10">
          <h1 className="text-xl font-bold tracking-tighter">
            Central Rentals <span className="font-normal text-muted-foreground ml-2 text-sm">{t("v2.tenant_portal_label", "Tenant portal")}</span>
          </h1>
          <Link
            to="/messages"
            data-testid="tenant-v2-quick-question-btn"
            className="text-sm font-medium flex items-center gap-2 hover:text-primary transition-colors"
          >
            <MessageSquare className="h-4 w-4" /> {t("v2.tenant_quick_q", "Quick question")}
          </Link>
          <Link
            to="/tenant/toolkit"
            data-testid="tenant-v2-toolkit-nav"
            className="hidden md:inline-flex text-sm font-medium items-center gap-2 hover:text-primary transition-colors ml-4"
          >
            <Sparkles className="h-4 w-4" /> {t("v2.tenant_toolkit", "Toolkit")}
          </Link>
        </header>

        <div className="max-w-4xl mx-auto p-6 md:p-10 space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-border pb-8">
            <div>
              <h2 className="text-3xl font-bold tracking-tight" data-testid="tenant-v2-greeting">{greeting}</h2>
              <p className="text-muted-foreground mt-1" data-testid="tenant-v2-due">{nextDueLabel}</p>
            </div>
            {score && (score.score != null) && (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-sm" data-testid="tenant-v2-score">
                <Star className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-widest">
                    {t("v2.tenant_score_label", "Rent-on-Time Score")}
                  </p>
                  <p className="text-xl font-bold text-emerald-900 dark:text-emerald-100 font-mono">{score.score}</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              data-testid="tenant-v2-pay-card"
              className="p-6 bg-card border border-border rounded-sm hover:border-primary transition-colors block"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-muted rounded-full"><CreditCard className="h-5 w-5 text-foreground" /></div>
                {dueDays != null && (
                  <span className="text-xs font-bold px-2 py-1 bg-muted rounded-sm text-foreground">
                    {dueDays <= 0 ? t("v2.tenant_due_pill_now", "Due now") : `${dueDays}d`}
                  </span>
                )}
              </div>
              <h3 className="font-bold text-lg">{t("v2.tenant_pay_rent", "Pay rent")}</h3>
              <p className="text-muted-foreground text-sm mt-1">
                {t("v2.tenant_pay_desc_p2p", "See your landlord's payment instructions and report your payment in one tap.")}
              </p>
              <Link
                to="/payments"
                data-testid="tenant-v2-pay-fallback-link"
                className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm transition-colors"
              >
                {t("v2.tenant_pay_open", "Open payments")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <Link
              to="/maintenance"
              data-testid="tenant-v2-maint-card"
              className="p-6 bg-card border border-border rounded-sm hover:border-primary transition-colors block"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-muted rounded-full"><PenTool className="h-5 w-5 text-foreground" /></div>
              </div>
              <h3 className="font-bold text-lg">{t("v2.tenant_maint", "Maintenance request")}</h3>
              <p className="text-muted-foreground text-sm mt-1">
                {t("v2.tenant_maint_desc", "Report an issue with photos. Your Copilot routes it to the right vendor.")}
              </p>
            </Link>

            <Link
              to="/tenant-community"
              data-testid="tenant-v2-community-card"
              className="p-6 bg-card border border-border rounded-sm hover:border-primary transition-colors block"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-muted rounded-full"><Users className="h-5 w-5 text-foreground" /></div>
              </div>
              <h3 className="font-bold text-lg">{t("v2.tenant_community", "Community")}</h3>
              <p className="text-muted-foreground text-sm mt-1">
                {t("v2.tenant_community_desc", "Discuss with neighbours, browse rentals nearby, and connect with vetted vendors.")}
              </p>
            </Link>

            <Link
              to="/rent-receipts"
              data-testid="tenant-v2-receipts-card"
              className="p-6 bg-card border border-border rounded-sm hover:border-primary transition-colors block"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-muted rounded-full"><Star className="h-5 w-5 text-foreground" /></div>
              </div>
              <h3 className="font-bold text-lg">{t("v2.tenant_receipts", "Rent receipts")}</h3>
              <p className="text-muted-foreground text-sm mt-1">
                {t("v2.tenant_receipts_desc", "Download PDFs for every rent payment. Great for taxes or your landlord references.")}
              </p>
            </Link>
          </div>

          {/* Smart features control room — tenant-side. Every card here
              is a live smart feature the tenant already has access to.
              We surface them prominently so they never have to hunt
              through the sidebar for the good stuff. */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-emerald-100 rounded-md"><Sparkles className="h-4 w-4 text-emerald-700"/></div>
              <h2 className="font-display text-xl font-bold text-foreground">Smart features · tenant control room</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-4">Every helper in Central Rentals — always on, always free for tenants.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/assistant" data-testid="tenant-smart-mark"
                className="group p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 hover:border-emerald-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><MessageSquare className="h-5 w-5 text-emerald-700"/></div>
                  <ArrowRight className="h-4 w-4 text-emerald-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Ask Mark anything</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Landlord-tenant law, RTA rules, moving checklists — answered instantly in plain English.</p>
              </Link>

              <Link to="/cra-filing" data-testid="tenant-smart-cra"
                className="group p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 hover:border-blue-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><Receipt className="h-5 w-5 text-blue-700"/></div>
                  <ArrowRight className="h-4 w-4 text-blue-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Tax season, sorted</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Downloadable rent receipts and ON-BEN eligibility — CRA-ready one-click PDF.</p>
              </Link>

              <Link to="/tenant/renewal" data-testid="tenant-smart-renewal"
                className="group p-5 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 hover:border-amber-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><FileText className="h-5 w-5 text-amber-700"/></div>
                  <ArrowRight className="h-4 w-4 text-amber-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Renewal & moving out</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Renew your lease, give notice, or coordinate a smooth move — all templates ready.</p>
              </Link>

              <Link to="/credit-builder" data-testid="tenant-smart-credit"
                className="group p-5 bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-200 hover:border-purple-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><ShieldCheck className="h-5 w-5 text-purple-700"/></div>
                  <ArrowRight className="h-4 w-4 text-purple-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Build your credit</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Report your on-time rent to the credit bureaus and grow your score every month.</p>
              </Link>

              <Link to="/maintenance" data-testid="tenant-smart-maint"
                className="group p-5 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 hover:border-rose-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><Wrench className="h-5 w-5 text-rose-700"/></div>
                  <ArrowRight className="h-4 w-4 text-rose-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Smart maintenance triage</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Snap a photo — Central Rentals routes it to the right vendor and tracks the fix.</p>
              </Link>

              <Link to="/messages" data-testid="tenant-smart-messages"
                className="group p-5 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 hover:border-sky-400 rounded-xl transition-all hover:shadow-md block">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm"><MessagesSquare className="h-5 w-5 text-sky-700"/></div>
                  <ArrowRight className="h-4 w-4 text-sky-700 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </div>
                <h3 className="font-bold text-base text-slate-900">Chat with your landlord</h3>
                <p className="text-sm text-slate-600 mt-1 leading-snug">Message with read receipts and translate to your language automatically.</p>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
